import type { GrowthCeilingResult } from '@messenger/utils'

/**
 * The measurement tolerance of the classic autosize, in px: how far painted
 * text may run past its frame before the loop fits it. Two renderers, or one
 * renderer on two loads, lay a line out a few px apart, and a fit for that
 * difference would shrink a name that stands in its frame on every other
 * surface. The load result and the author's own resize (`fitTextIntoBox`)
 * share it: text past the frame by no more than this keeps its authored
 * size; text further past it is shrunk into the frame.
 */
export const GRACE_DISTANCE = 10

/** A stored coordinate as a number; a stored string is parsed, anything else is 0. */
export const pxOf = (value: unknown): number => {
  const n = typeof value === 'string' ? Number.parseFloat(value) : value
  return typeof n === 'number' && Number.isFinite(n) ? n : 0
}

/**
 * What the load result did with the text inside the row's frame: `kept`, the
 * text stands inside the frame at its authored size; `grace`, it runs past
 * the frame by no more than the grace and keeps its size; `fitted`, it runs
 * further and is shrunk into the frame.
 */
export type TextBoxLoadFit = 'kept' | 'grace' | 'fitted'

export type TextBoxLoadResult = {
  /**
   * The frame the box paints: the row's stored height. A row with no stored
   * height takes its text's painted height, there being no other number,
   * which is what the pre-rule editor wrote on init.
   */
  height: number
  /**
   * The room the page leaves under the box (`growthCeiling`), for the flag
   * and the record alone; null when the surface could not say what lies
   * under it. Nothing applies it to the frame.
   */
  ceiling: number | null
  boundedBy: string | null
  /**
   * Set when the stored frame is past its room (`boundedBy` `preexisting`):
   * the room, what bounds it, and one line of the box's text.
   */
  kept?: GrowthCeilingResult['kept']
  /** True when the text must be fitted into `height` because it painted past the grace. */
  fitted: boolean
  fit: TextBoxLoadFit
  /** How far the text painted past the frame, in px; 0 when it did not. */
  overflow: number
}

/** The stamp a result leaves for the probe: `grace:<overflow px>`, else the fit by name. */
export const textBoxLoadFitStamp = (result: TextBoxLoadResult): string =>
  result.fit === 'grace' ? `grace:${result.overflow.toFixed(2)}` : result.fit

/**
 * The one load result for a TinyMCE text box: the frame is the row's stored
 * height on every surface, and the text is fitted into it — text past the
 * frame by no more than the grace keeps its size, text further past it is
 * shrunk into the frame by the classic loop (`fitTextIntoBox`, strict). The
 * room under the box (`useEditorTextBoxGrowthCeiling`, the same measurement
 * a Lotus box takes) rides along as a record: a stored frame past that room
 * is flagged `preexisting` with the numbers a refit would need, and the
 * frame still paints as saved. Without a stored frame the box takes its
 * text's height. Nothing crashes. The result is a paint: the surfaces size
 * the box by it and none of them stores it.
 */
export const textBoxLoadHeight = ({
  contentHeight,
  storedHeight,
  ceiling,
  grace = GRACE_DISTANCE,
}: {
  contentHeight: number
  storedHeight: number
  ceiling: GrowthCeilingResult | null
  grace?: number
}): TextBoxLoadResult => {
  const stored = storedHeight > 0 ? storedHeight : 0
  const height = stored > 0 ? stored : contentHeight
  const record =
    ceiling === null
      ? { ceiling: null, boundedBy: null }
      : {
          ceiling: ceiling.maxHeight,
          boundedBy: ceiling.boundedBy,
          ...(ceiling.kept ? { kept: ceiling.kept } : {}),
        }
  const overflow = Math.max(0, contentHeight - height)
  if (overflow === 0)
    return { height, ...record, fitted: false, fit: 'kept', overflow: 0 }
  if (overflow <= grace)
    return { height, ...record, fitted: false, fit: 'grace', overflow }
  return { height, ...record, fitted: true, fit: 'fitted', overflow }
}
